<template>
  <div class="planning-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <h2>📅 Planning d'Installation & RDV</h2>
        <p class="page-subtitle">Gérez les demandes clients et planifiez les interventions</p>
      </div>
      <div class="header-actions">
        <button 
          v-if="['Commercial', 'Admin', 'Responsable Planning'].includes(userRole)"
          class="btn btn-primary"
          @click="showCreateProspectModal = true"
        >
          <i class="fas fa-plus"></i>
          📝 Nouvelle Demande
        </button>
        <button 
          v-if="userRole === 'Responsable Planning'"
          class="btn btn-success"
          @click="showCreateRdvModal = true"
          :disabled="!selectedProspect"
        >
          <i class="fas fa-calendar-plus"></i>
          📅 Planifier RDV
        </button>
      </div>
    </div>

    <!-- Navigation Tabs -->
    <div class="nav-tabs-container">
      <div class="nav-tabs">
        <button 
          v-if="['Commercial', 'Admin', 'Responsable Planning'].includes(userRole)"
          :class="['nav-tab', { active: activeTab === 'prospects' }]"
          @click="activeTab = 'prospects'"
        >
          <i class="fas fa-user-tie"></i>
          <span>🎯 Prospects</span>
          <span v-if="prospectsCount > 0" class="badge">{{ prospectsCount }}</span>
        </button>
        <button 
          v-if="['Responsable Planning', 'Technicien', 'Admin'].includes(userRole)"
          :class="['nav-tab', { active: activeTab === 'planning' }]"
          @click="activeTab = 'planning'"
        >
          <i class="fas fa-calendar-alt"></i>
          <span>📋 Planning</span>
          <span v-if="rdvCount > 0" class="badge badge-info">{{ rdvCount }}</span>
        </button>
      </div>
    </div>

    <!-- Content Area -->
    <div class="content-area">
      <!-- Prospects Tab (Commercial) -->
      <div v-if="activeTab === 'prospects'" class="tab-content">
        <ProspectsManager 
          ref="prospectsManagerRef"
          :user-role="userRole"
          @prospect-selected="handleProspectSelected"
          @prospects-count="prospectsCount = $event"
        />
      </div>

      <!-- Planning Tab (Responsable Planning & Technicien) -->
      <div v-if="activeTab === 'planning'" class="tab-content">
        <PlanningManager 
          :user-role="userRole"
          :view-mode="planningViewMode"
          @rdv-count="rdvCount = $event"
          @view-changed="planningViewMode = $event"
        />
      </div>
    </div>

    <!-- Modals -->
    <CreateProspectModal 
      v-if="showCreateProspectModal"
      @close="showCreateProspectModal = false"
      @prospect-created="handleProspectCreated"
    />

    <CreateRdvModal 
      v-if="showCreateRdvModal"
      :prospect="selectedProspect"
      @close="showCreateRdvModal = false"
      @rdv-created="handleRdvCreated"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'
import ProspectsManager from '../components/planning/ProspectsManager.vue'
import PlanningManager from '../components/planning/PlanningManager.vue'
import CreateProspectModal from '../components/planning/CreateProspectModal.vue'
import CreateRdvModal from '../components/planning/CreateRdvModal.vue'

export default {
  name: 'Planning',
  components: {
    ProspectsManager,
    PlanningManager,
    CreateProspectModal,
    CreateRdvModal
  },
  setup() {
    const store = useStore()
    const toast = useToast()

    // État local
    const activeTab = ref('prospects')
    const planningViewMode = ref('calendrier') // calendrier, kanban, liste
    const showCreateProspectModal = ref(false)
    const showCreateRdvModal = ref(false)
    const selectedProspect = ref(null)
    
    // Références aux composants
    const prospectsManagerRef = ref(null)

    // Compteurs pour les badges
    const prospectsCount = ref(0)
    const rdvCount = ref(0)



    // Utilisateur connecté
    const user = computed(() => store.getters['auth/user'])
    const userRole = computed(() => user.value?.role || '')

    // Déterminer l'onglet par défaut selon le rôle
    const setDefaultTab = () => {
      switch (userRole.value) {
        case 'Commercial':
          activeTab.value = 'prospects'
          break
        case 'Responsable Planning':
          activeTab.value = 'planning'
          break
        case 'Technicien':
          activeTab.value = 'planning'
          break
        default:
          activeTab.value = 'prospects'
      }
    }

    // Gestionnaires d'événements
    const handleProspectSelected = (prospect) => {
      selectedProspect.value = prospect
    }

    const handleProspectCreated = () => {
      showCreateProspectModal.value = false
      toast.success('✅ Demande créée avec succès')
      // Rafraîchir la liste des prospects
      if (prospectsManagerRef.value && prospectsManagerRef.value.loadProspects) {
        prospectsManagerRef.value.loadProspects()
      }
    }

    const handleProspectValidated = () => {
      toast.success('✅ Prospect validé avec succès')
      // Rafraîchir les listes
    }

    const handleRdvCreated = () => {
      showCreateRdvModal.value = false
      selectedProspect.value = null
      toast.success('📅 RDV planifié avec succès')
      // Basculer vers l'onglet planning
      activeTab.value = 'planning'
    }

    onMounted(() => {
      setDefaultTab()
    })

    return {
      // État
      activeTab,
      planningViewMode,
      showCreateProspectModal,
      showCreateRdvModal,
      selectedProspect,
      
      // Références
      prospectsManagerRef,
      
      // Compteurs
      prospectsCount,
      rdvCount,

      
      // Computed
      userRole,
      
      // Méthodes
      handleProspectSelected,
      handleProspectCreated,
      handleProspectValidated,
      handleRdvCreated
    }
  }
}
</script>

<style scoped>
.planning-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
  height: calc(100vh - 140px);
  overflow-y: auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--light-gray);
}

.header-content h2 {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--dark-gray);
  margin-bottom: 0.5rem;
}

.page-subtitle {
  color: #6c757d;
  margin: 0;
  font-size: 1rem;
}

.header-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

/* Navigation Tabs avec design moderne */
.nav-tabs-container {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 0.6rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.nav-tabs {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.nav-tabs::-webkit-scrollbar {
  display: none;
}

.nav-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: 2px solid transparent;
  border-radius: 12px;
  background: transparent;
  color: var(--primary-blue);
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  position: relative;
}

.nav-tab:hover {
  background: rgba(0, 27, 183, 0.05);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.1);
}

.nav-tab.active {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-color: var(--primary-blue);
  box-shadow: 0 6px 20px rgba(0, 27, 183, 0.3);
}

.nav-tab.active:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.4);
}

.nav-tab i {
  font-size: 1rem;
}

.badge {
  background: linear-gradient(135deg, #ff6b6b, #ee5a24);
  color: white;
  border-radius: 12px;
  padding: 0.2rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 700;
  min-width: 20px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
}

.badge.badge-warning {
  background: linear-gradient(135deg, #f39c12, #e67e22);
}

.badge.badge-info {
  background: linear-gradient(135deg, #3498db, #2980b9);
}

.badge.badge-success {
  background: linear-gradient(135deg, #27ae60, #229954);
}

.nav-tab.active .badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.2);
}

.content-area {
  min-height: 400px;
}

.tab-content {
  animation: fadeInUp 0.4s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .header-actions {
    justify-content: center;
  }

  .nav-tabs {
    justify-content: flex-start;
    padding-bottom: 0.5rem;
  }

  .nav-tab {
    padding: 0.6rem 1rem;
    font-size: 0.8rem;
  }

  .nav-tab span:not(.badge) {
    display: none;
  }

  .nav-tab i {
    font-size: 1.1rem;
  }
}

@media (max-width: 480px) {
  .planning-page {
    padding: 0 0.5rem;
  }

  .nav-tabs-container {
    padding: 0.5rem;
  }

  .nav-tab {
    padding: 0.5rem 0.8rem;
  }
}
</style>
